import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, QrCode, Route } from "lucide-react";

export default function SettingsPage() {
  const qc = useQueryClient();
  const { isAdmin, isManager, restaurant } = useAuth();
  const canEdit = isAdmin || isManager;
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (data) setForm({ ...data, slug: data.slug || restaurant?.slug || "" });
  }, [data, restaurant?.slug]);

  if (!form) return <div className="p-8 text-neutral-400">Loading…</div>;

  const save = async () => {
    const body = {
      restaurant_name: form.restaurant_name, address: form.address, phone: form.phone,
      gst_number: form.gst_number, default_tax_rate: Number(form.default_tax_rate),
      currency: form.currency, currency_symbol: form.currency_symbol, loyalty_rate: Number(form.loyalty_rate),
      kitchen_order_flow: form.kitchen_order_flow === "approve" ? "approve" : "direct",
      upi_payload: form.upi_payload || "",
      loyalty_redeem_value: Number(form.loyalty_redeem_value || 1),
      service_charge_rate: Number(form.service_charge_rate || 0),
      hours: form.hours || "",
      about: form.about || "",
      whatsapp_enabled: form.whatsapp_enabled !== false,
      whatsapp_auto: form.whatsapp_auto !== false,
    };
    await api.put("/settings", body); toast.success("Settings saved");
    qc.invalidateQueries({ queryKey: ["settings"] });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Settings" subtitle="Restaurant profile, kitchen routing & UPI">
        {canEdit && <Button onClick={save} data-testid="save-settings" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Save className="w-4 h-4 mr-1" /> Save</Button>}
      </PageHeader>

      <div className="max-w-2xl space-y-5">
        <div className="kul-card p-6 space-y-4">
          <h3 className="font-display font-700 text-lg">Restaurant Profile</h3>
          <div><Label>Restaurant Name</Label><Input value={form.restaurant_name} onChange={set("restaurant_name")} disabled={!canEdit} data-testid="set-name" /></div>
          <div><Label>Address</Label><Input value={form.address} onChange={set("address")} disabled={!canEdit} data-testid="set-address" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} disabled={!canEdit} /></div>
            <div><Label>GST Number</Label><Input value={form.gst_number} onChange={set("gst_number")} disabled={!canEdit} data-testid="set-gst" /></div>
          </div>
        </div>

        <div className="kul-card p-6 space-y-4">
          <h3 className="font-display font-700 text-lg flex items-center gap-2"><Route className="w-5 h-5 text-[var(--kul-primary)]" /> Kitchen routing</h3>
          <p className="text-sm text-neutral-500">How waiter tickets reach the kitchen.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: "direct", title: "Waiter → Kitchen", desc: "Tickets go straight to the kitchen." },
              { key: "approve", title: "Waiter → Manager → Kitchen", desc: "Manager must approve before kitchen sees the ticket." },
            ].map((opt) => (
              <button key={opt.key} type="button" disabled={!canEdit} onClick={() => setForm({ ...form, kitchen_order_flow: opt.key })}
                data-testid={`flow-${opt.key}`}
                className={`text-left rounded-2xl border p-4 transition-colors ${form.kitchen_order_flow === opt.key ? "border-[var(--kul-primary)] bg-[var(--kul-primary)]/8" : "border-neutral-200"}`}>
                <div className="font-semibold">{opt.title}</div>
                <div className="text-xs text-neutral-500 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="kul-card p-6 space-y-4">
          <h3 className="font-display font-700 text-lg flex items-center gap-2"><QrCode className="w-5 h-5 text-[var(--kul-primary)]" /> Waiter UPI QR</h3>
          <p className="text-sm text-neutral-500">
            Paste your Google Business / UPI QR payload or UPI ID. The waiter portal will generate a new QR for the exact bill amount (e.g. ₹999).
          </p>
          <div>
            <Label>UPI ID or QR text</Label>
            <textarea value={form.upi_payload || ""} onChange={set("upi_payload")} disabled={!canEdit}
              rows={3} data-testid="set-upi"
              placeholder="shop@okaxis  or  upi://pay?pa=shop@okaxis&pn=My Cafe"
              className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          {form.upi_vpa && <p className="text-xs text-green-700">Linked VPA: {form.upi_vpa}{form.upi_payee_name ? ` · ${form.upi_payee_name}` : ""}</p>}
        </div>

        <div className="kul-card p-6 space-y-4">
          <h3 className="font-display font-700 text-lg">Tax, Currency & Loyalty</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Default GST %</Label><Input type="number" value={form.default_tax_rate} onChange={set("default_tax_rate")} disabled={!canEdit} data-testid="set-tax" /></div>
            <div><Label>Loyalty pts / ₹100</Label><Input type="number" value={form.loyalty_rate} onChange={set("loyalty_rate")} disabled={!canEdit} data-testid="set-loyalty" /></div>
            <div><Label>₹ per loyalty point (redeem)</Label><Input type="number" value={form.loyalty_redeem_value || 1} onChange={set("loyalty_redeem_value")} disabled={!canEdit} /></div>
            <div><Label>Service charge %</Label><Input type="number" value={form.service_charge_rate || 0} onChange={set("service_charge_rate")} disabled={!canEdit} /></div>
            <div><Label>Currency Code</Label><Input value={form.currency} onChange={set("currency")} disabled={!canEdit} /></div>
            <div><Label>Currency Symbol</Label><Input value={form.currency_symbol} onChange={set("currency_symbol")} disabled={!canEdit} /></div>
            <div className="col-span-2"><Label>Hours</Label><Input value={form.hours || ""} onChange={set("hours")} disabled={!canEdit} /></div>
            <div className="col-span-2"><Label>About (website)</Label><Input value={form.about || ""} onChange={set("about")} disabled={!canEdit} /></div>
          </div>
        </div>

        <div className="kul-card p-6 space-y-4">
          <h3 className="font-display font-700 text-lg">WhatsApp</h3>
          <p className="text-sm text-neutral-500">Customer order confirmations, ready alerts, and bills via WhatsApp Business (Twilio). Without credentials, messages are logged.</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.whatsapp_enabled !== false} disabled={!canEdit}
              onChange={(e) => setForm({ ...form, whatsapp_enabled: e.target.checked })} data-testid="set-wa-enabled" />
            Enable WhatsApp messaging
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.whatsapp_auto !== false} disabled={!canEdit}
              onChange={(e) => setForm({ ...form, whatsapp_auto: e.target.checked })} data-testid="set-wa-auto" />
            Auto-send on order / ready / payment
          </label>
        </div>

        <div className="kul-card p-6 space-y-2 text-sm">
          <h3 className="font-display font-700 text-lg">Guest surfaces</h3>
          {form.slug ? (
            <>
              <p className="text-neutral-500">Your public URL prefix: <code className="text-neutral-800">/r/{form.slug}</code></p>
              <a className="block text-[var(--kul-primary)]" href={`/r/${form.slug}/order`} target="_blank" rel="noreferrer">Restaurant website / online order</a>
              <a className="block text-[var(--kul-primary)]" href={`/r/${form.slug}/kiosk`} target="_blank" rel="noreferrer">Self-service kiosk</a>
              <a className="block text-[var(--kul-primary)]" href={`/r/${form.slug}/tokens`} target="_blank" rel="noreferrer">Token TV display</a>
              <a className="block text-[var(--kul-primary)]" href={`/r/${form.slug}/feedback`} target="_blank" rel="noreferrer">Feedback QR page</a>
              <p className="text-neutral-500">Table Scan &amp; Order QR: Tables → QR on each table (opens /r/{form.slug}/scan/:id)</p>
            </>
          ) : (
            <p className="text-neutral-500">Sign up a restaurant to get guest links on /r/your-slug.</p>
          )}
        </div>
        {!canEdit && <p className="text-sm text-neutral-400">Only admins and managers can edit settings.</p>}
      </div>
    </div>
  );
}
